import { Link } from "react-router-dom";
import {
  Sparkles, Users, TreeDeciduous, PartyPopper, BookHeart,
  Flower, ArrowRight, Heart,
} from "lucide-react";

// -----------------------------------------------------------------------------
// IftIntroArticle — a warm, conversion-focused introductory block for
// IndianFamilyTree.com. Meant to sit on the child site's homepage or
// on any landing that wants to explain the vision before showing the
// pricing / signup. Two CTAs: build a tree (→ /heritage) and create a
// free memorial (→ /obituaries/submit). Full plan matrix is at the
// bottom with a link to /pricing.
//
// Uses no site-specific data — it is safe to render anywhere, but the
// parent should generally only mount it on the IFT child site so the
// copy ("indianfamilytree.com") matches the visitor's domain.
// -----------------------------------------------------------------------------

export default function IftIntroArticle() {
  return (
    <section
      className="max-w-5xl mx-auto px-6 lg:px-10 py-16"
      data-testid="ift-intro-article"
    >
      <div className="text-center max-w-3xl mx-auto">
        <div className="overline mb-3" style={{ color: "#EA580C" }}>
          A DIGITAL SANCTUARY FOR EVERY INDIAN HOME
        </div>
        <h1
          className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.03]"
          data-testid="ift-intro-h1"
        >
          A living archive for
          <br className="hidden md:block" />
          <span style={{ color: "#EA580C" }}> every Indian family.</span>
        </h1>
        <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
          <strong>IndianFamilyTree.com</strong> serves as a digital sanctuary
          designed to preserve the rich, multi-generational narratives of
          Indian households. By bridging historical heritage with modern
          technology, this archive provides a dedicated space for every Indian
          family home to document, secure, and celebrate their lineage. It
          offers intuitive tools to map complex family structures, archive
          precious photographs, obituaries, celebrate every life memorials,
          and record oral histories, store family documents — ensuring that
          personal legacies remain vibrant and accessible for future generations.
        </p>
      </div>

      <div className="mt-16 grid md:grid-cols-2 gap-10 items-start">
        <div className="prose prose-neutral prose-lg max-w-none prose-headings:font-display">
          <p>
            In every Indian home, family is more than just a structure — it is
            a tapestry of traditions, values, stories, and shared memories
            spanning generations. From the native villages (<em>Gaon</em> or{" "}
            <em>Tharavadu</em>) our ancestors called home, to the vibrant
            family gatherings (<em>Kudumba Sangamam</em>) that bring us
            together today, our lineages shape who we are.
          </p>
          <p>
            Yet, as families grow, migrate, and scatter across the globe,
            staying connected to our roots can become challenging. Younger
            generations often lose touch with their extended heritage, and
            precious stories of our ancestors risk being forgotten over time.
          </p>

          <h3>A platform built for the Indian family context</h3>
          <p>
            Generic family tree builders often miss the cultural nuances of
            Indian households. IndianFamilyTree.com is designed from the
            ground up with features that reflect our rich heritage:
          </p>
          <ul>
            <li>
              <strong>Cultural metadata</strong> — record Gotra, Gaon/Tharavadu,
              Kuldevi/Kuldevta, and native languages alongside basic profile
              details.
            </li>
            <li>
              <strong>Multi-branch navigation</strong> — map complex paternal
              (Dadi/Nani) and maternal side lineages seamlessly across generations.
            </li>
            <li>
              <strong>Collaborative building</strong> — invite relatives via
              WhatsApp with a single tap to help fill in missing branches,
              upload photos, and verify family details.
            </li>
          </ul>
        </div>

        <aside className="space-y-4">
          <div
            className="rounded-3xl p-6"
            style={{
              background: "linear-gradient(135deg, rgba(234, 88, 12, 0.10), rgba(139, 92, 246, 0.06))",
              border: "1px solid rgba(234, 88, 12, 0.28)",
            }}
          >
            <div className="overline mb-2" style={{ color: "#EA580C" }}>
              FREE FOREVER
            </div>
            <h3 className="font-display text-2xl">Celebrate a Life — free digital memorials</h3>
            <p className="mt-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              Digital life story · Anniversary & Tithi reminders · Shared photo wall ·
              Virtual candles · Direct link into your family tree.
              Because preserving an ancestor's memory should never cost a family a single rupee.
            </p>
            <Link
              to="/obituaries/submit"
              data-testid="ift-intro-cta-memorial"
              className="mt-4 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-white transition-transform hover:scale-[1.02]"
              style={{ background: "#EA580C" }}
            >
              <Flower className="w-4 h-4" /> Create a Free Memorial
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div
            className="rounded-3xl p-6"
            style={{
              background: "rgba(16, 185, 129, 0.06)",
              border: "1px solid rgba(16, 185, 129, 0.28)",
            }}
          >
            <div className="overline mb-2" style={{ color: "#059669" }}>
              BEGIN YOUR TREE
            </div>
            <h3 className="font-display text-2xl">Start with 20 members — free</h3>
            <p className="mt-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              Perfect for nuclear family mapping. Invite relatives via WhatsApp
              and grow it into a multi-branch clan tree at your own pace.
            </p>
            <Link
              to="/heritage"
              data-testid="ift-intro-cta-tree"
              className="mt-4 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-white transition-transform hover:scale-[1.02]"
              style={{ background: "#059669" }}
            >
              <TreeDeciduous className="w-4 h-4" /> Start My Family Tree
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </aside>
      </div>

      {/* Plan matrix */}
      <div className="mt-20" data-testid="ift-intro-plans">
        <div className="text-center max-w-2xl mx-auto">
          <div className="overline mb-2" style={{ color: "#EA580C" }}>
            FLEXIBLE PLANS FOR EVERY FAMILY SIZE
          </div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight">
            One free tier. Three thoughtful upgrades.
          </h2>
        </div>
        <div className="mt-8 overflow-x-auto">
          <table className="w-full border rounded-2xl overflow-hidden" style={{ borderColor: "var(--wofy-border)" }}>
            <thead style={{ background: "rgba(234, 88, 12, 0.08)" }}>
              <tr>
                <th className="text-left px-4 py-3 font-mono uppercase tracking-widest text-[11px]">Plan</th>
                <th className="text-left px-4 py-3 font-mono uppercase tracking-widest text-[11px]">Node limit</th>
                <th className="text-left px-4 py-3 font-mono uppercase tracking-widest text-[11px]">
                  Price
                  <div className="mt-0.5 text-[10px] font-mono normal-case tracking-wide" style={{ color: "#EA580C" }}>
                    Yearly
                  </div>
                </th>
                <th className="text-left px-4 py-3 font-mono uppercase tracking-widest text-[11px]">Key highlight</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {[
                { name: "Free",      limit: "Up to 20 members",  price: "₹0",   note: "Perfect for nuclear family tree mapping" },
                { name: "Advance",   limit: "Up to 100 members", price: "₹299", note: "Ideal for extended families & photo archives" },
                { name: "Pro",       limit: "Up to 300 members", price: "₹499", note: "Deep multi-generational lineage mapping · Message Members" },
                { name: "Unlimited & Reunion", limit: "Unlimited members", price: "₹999", note: "Complete Family Reunion Management System · Member Chat & Messaging" },
              ].map((r, i) => (
                <tr key={r.name} className="border-t" style={{ borderColor: "var(--wofy-border)", background: i % 2 ? "transparent" : "rgba(0,0,0,0.02)" }}>
                  <td className="px-4 py-3 font-display text-base">{r.name}</td>
                  <td className="px-4 py-3">{r.limit}</td>
                  <td className="px-4 py-3 font-semibold" style={{ color: "#EA580C" }}>{r.price}</td>
                  <td className="px-4 py-3" style={{ color: "var(--wofy-text-soft)" }}>{r.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          <PartyPopper className="w-4 h-4 inline-block mr-1" style={{ color: "#EA580C" }} />
          With the Unlimited Plan, organising a family reunion (<em>Kudumba Sangamam</em>)
          becomes effortless — integrated RSVP tracking, event budget pooling,
          schedule planners, and a shared post-event memory wall.
        </p>
        <div className="mt-6 text-center">
          <Link
            to="/pricing"
            data-testid="ift-intro-cta-pricing"
            className="wofy-btn-primary inline-flex items-center gap-2 !text-base !px-6 !py-3"
            style={{ background: "#EA580C" }}
          >
            See full pricing & choose a plan <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Celebrate a Life — long block */}
      <div className="mt-20 max-w-4xl mx-auto prose prose-neutral prose-lg prose-headings:font-display">
        <h2>"Celebrate a Life" — Free Digital Memorials</h2>
        <p>
          Our ancestors paved the path we walk today, and preserving their
          memory is at the core of family history.
        </p>
        <p>
          With our integrated "Celebrate a Life" memorial feature —{" "}
          <strong>completely FREE for all users</strong> — you can create
          dedicated, permanent digital tribute pages attached directly to your
          family tree nodes.
        </p>

        <h3>What you can do with "Celebrate a Life"</h3>
        <ul>
          <li>
            <strong>Digital Life Story & Biography</strong> — document your
            loved one's journey, achievements, values, and legacy in detail.
          </li>
          <li>
            <strong>Anniversary & Remembrance Reminders</strong> — keep the
            family connected around important dates like Birthdays, Death
            Anniversaries, and annual remembrance rituals (Tithi / Punya Tithi).
          </li>
          <li>
            <strong>Shared Memory & Photo Wall</strong> — allow relatives
            across the world to post heartfelt tributes, light a virtual
            candle, share stories, and upload rare vintage photographs.
          </li>
          <li>
            <strong>Linked Heritage</strong> — because the memorial is directly
            attached to your family tree, younger generations can click on any
            ancestor's profile and immediately learn about their life,
            character, and impact.
          </li>
        </ul>

        <h2>Start Building Your Legacy Today</h2>
        <p>
          Your family's history is an irreplaceable gift for future
          generations. Whether you want to map your lineage, honor a beloved
          family member, or organise your next big family reunion,
          <strong> IndianFamilyTree.com</strong> gives you the tools to keep
          your roots strong and connected.
        </p>
        <p>
          <em>
            Begin mapping your family tree and create a free memorial today at{" "}
            <strong>IndianFamilyTree.com</strong>.
          </em>
        </p>
      </div>

      <div className="mt-10 flex flex-wrap justify-center gap-3">
        <Link
          to="/heritage"
          className="wofy-btn-primary inline-flex items-center gap-2 !text-base !px-5 !py-3"
          style={{ background: "#EA580C" }}
        >
          <TreeDeciduous className="w-4 h-4" /> Start My Family Tree <ArrowRight className="w-4 h-4" />
        </Link>
        <Link
          to="/obituaries/submit"
          className="wofy-btn-ghost inline-flex items-center gap-2 !text-base"
        >
          <Flower className="w-4 h-4" /> Create a Free Memorial
        </Link>
        <Link
          to="/pricing"
          className="wofy-btn-ghost inline-flex items-center gap-2 !text-base"
        >
          <Sparkles className="w-4 h-4" /> Compare Plans
        </Link>
      </div>
    </section>
  );
}
